
import React, {
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { Dialog } from '@glowforge/ui-common/components';
import {
  widgetDefaultProps,
  widgetPropType,
} from '@glowforge/ui-common/models';
import useLocalStorage from '../../hooks/use-local-storage';
import ModalPriorityContext from '../../context/ModalPriorityContext/ModalPriorityContext';

import SignupWidget from './signup-widget-rebrand';
import './signup-modal-rebrand.less';

/**
 * Unique ID for this modal
 */
const MODAL_ID = 'signup';

/**
 * How long to wait before showing modal
 */
const OPEN_DELAY = 10000;

/**
 * Max number of times a user will see the modal
 */
const SHOW_MAX_TIMES = 3;

/**
 * How many days to wait before showing the modal again
 */
const SHOW_AGAIN_AFTER_DAYS = 3;
const MS_PER_DAY = 86400000;

/**
 * Storage keys used to persist state
 */
const DISMISSED_AT_KEY = '_gf_signup_modal_dismissed_at';
const DISMISSED_COUNT_KEY = '_gf_signup_modal_dismissed_count';
const SUBMITTED_KEY = '_gf_signup_modal_submitted';

/**
 * Shows a modal that accepts name, email and phone number followed by a thank
 * you message after submission
 */
const SignupModal = (props) => {
  const [submitted] = useLocalStorage(SUBMITTED_KEY, false);
  const [dismissedAt, setDismissedAt] = useLocalStorage(DISMISSED_AT_KEY, 0);
  const [dismissedCount, setDismissedCount] = useLocalStorage(DISMISSED_COUNT_KEY, 0);
  const { isModalOpen, openModal, closeModal } = useContext(ModalPriorityContext);

  /**
   * When component first renders, fetch referral info and then show the dialog
   * if the user hasn't seen it for X days
   */
  useEffect(() => {
    const daysSinceDismissed = (Date.now() - dismissedAt) / MS_PER_DAY;
    if (
      submitted
      || daysSinceDismissed < SHOW_AGAIN_AFTER_DAYS
      || dismissedCount >= SHOW_MAX_TIMES
    ) {
      return () => { };
    }
    const timer = setTimeout(() => openModal(MODAL_ID), OPEN_DELAY);
    return () => {
      clearTimeout(timer);
      closeModal(MODAL_ID);
    };
  }, []);

  /**
   * Close modal and track so we can decide when to show it again in the future
   */
  const close = useCallback(() => {
    setDismissedCount(dismissedCount + 1);
    setDismissedAt(Date.now());
    closeModal(MODAL_ID);
  }, [setDismissedCount, dismissedCount, setDismissedAt, closeModal]);

  /**
   * Handle click on the close button
   */
  const onClose = useCallback(() => {
    close();
  }, [close]);

  return (
    <Dialog
      className='Signup SignupModal'
      overlayClassName='SignupModalOverlay'
      withScrollBox={false}
      onRequestClose={onClose}
      isOpen={isModalOpen(MODAL_ID)}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <SignupWidget {...props} onClose={onClose} />
    </Dialog>
  );
};

SignupModal.propTypes = widgetPropType;
SignupModal.defaultProps = widgetDefaultProps;

export default SignupModal;
